import React, { useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Save, Download, Star } from "lucide-react";

/* BP-002 Deal Intelligence tab — Wholesale analysis end-to-end.

   Layout:
     • Left column  — inputs
     • Right column — results / warnings / actions
     • Below       — saved scenarios + on-demand AI
*/

const DEFAULT_INPUTS = {
  arv: "", arvSource: "manual", arvConfidence: "0.70",
  askingPrice: "", repairCost: "", repairSource: "manual",
  buyerRulePercent: "0.70", targetAssignmentFee: "15000",
  buyerClosingCosts: "5000", buyerHoldingCosts: "0",
  buyerFinancingCosts: "0", buyerResaleCosts: "0",
  sellerClosingCosts: "3000", sellerPayoffs: "0", sellerArrears: "0",
  offerCushion: "0",
};

const CLASS_COLORS = {
  excellent: "bg-emerald-100 text-emerald-800 border-emerald-300",
  strong:    "bg-emerald-100 text-emerald-700 border-emerald-300",
  review:    "bg-amber-100  text-amber-700  border-amber-300",
  weak:      "bg-orange-100 text-orange-700 border-orange-300",
  pass:      "bg-red-100    text-red-700    border-red-300",
};

// Human-friendly Deal Score label + dot color per classification band.
// Backend `classification` stays canonical; this map is display-only.
const CLASS_MAP = {
  excellent: { label: "EXCELLENT DEAL", dot: "bg-emerald-500" },
  strong:    { label: "STRONG DEAL",    dot: "bg-emerald-500" },
  review:    { label: "GOOD DEAL",      dot: "bg-amber-500"   },
  weak:      { label: "WEAK DEAL",      dot: "bg-orange-500"  },
  pass:      { label: "PASS",           dot: "bg-red-500"     },
};

export default function DealIntelligenceTab({ property }) {
  const [inputs, setInputs] = useState({
    ...DEFAULT_INPUTS,
    arv: property?.estimated_arv || "",
    repairCost: property?.estimated_repairs || "",
    askingPrice: property?.asking_price || "",
  });
  const [scenarioName, setScenarioName] = useState("Wholesale - Base Case");
  const [result, setResult] = useState(null);
  const [scenario, setScenario] = useState(null);   // saved scenario doc
  const [aiLoading, setAiLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  const setInput = (k) => (e) => setInputs((f) => ({ ...f, [k]: e.target.value }));

  const submit = useCallback(async (save = false) => {
    if (!inputs.arv || parseFloat(inputs.arv) <= 0) {
      toast.error("Enter an ARV greater than $0.");
      return;
    }
    const setter = save ? setSaving : setCalculating;
    setter(true);
    try {
      const payload = {
        scenarioName,
        save,
        inputs: {
          ...inputs,
          arv: inputs.arv,
          arvConfidence: inputs.arvConfidence || "0.5",
          askingPrice: inputs.askingPrice || null,
          repairCost: inputs.repairCost || "0",
        },
      };
      const { data } = await api.post(
        `/v3/properties/${property.id}/wholesale/calculate`,
        payload,
      );
      setResult(data);
      if (save) {
        setScenario(data);
        toast.success("Scenario saved");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Calculation failed");
    } finally {
      setter(false);
    }
  }, [inputs, scenarioName, property.id]);

  const generateAi = useCallback(async () => {
    if (!scenario) {
      toast.error("Save the scenario first to generate an AI narrative.");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post(
        `/v3/wholesale/scenarios/${scenario.id}/ai-analysis`,
      );
      setScenario(data);
      setResult(data);
      toast.success("AI recommendation generated");
    } catch (err) {
      toast.error("AI analysis failed");
    } finally {
      setAiLoading(false);
    }
  }, [scenario]);

  const exportPdf = useCallback(() => {
    if (!scenario) {
      toast.error("Save the scenario first.");
      return;
    }
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/wholesale/scenarios/${scenario.id}/export/internal-pdf`,
      "_blank",
    );
  }, [scenario]);

  const outputs = result?.outputs || null;
  const classification = outputs?.classification || null;

  return (
    <div className="grid gap-5 lg:grid-cols-[420px_1fr]" data-testid="tab-panel-deal_intelligence">
      {/* Left column — inputs */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Assumptions</div>
        </div>
        <div className="space-y-2">
          <F label="Scenario name">
            <input value={scenarioName} onChange={(e)=>setScenarioName(e.target.value)}
                   data-testid="wholesale-scenario-name"
                   className="inp" />
          </F>
          <F label="ARV *" hint="After-Repair Value">
            <input type="number" min="0" step="1000" value={inputs.arv} onChange={setInput("arv")}
                   data-testid="wholesale-input-arv" className="inp" />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="ARV source">
              <select value={inputs.arvSource} onChange={setInput("arvSource")}
                      data-testid="wholesale-arv-source" className="inp">
                <option value="manual">manual</option>
                <option value="comps">comps</option>
                <option value="appraisal">appraisal</option>
                <option value="broker_opinion">broker opinion</option>
              </select>
            </F>
            <F label="ARV conf. (0-1)">
              <input type="number" min="0" max="1" step="0.05" value={inputs.arvConfidence}
                     onChange={setInput("arvConfidence")}
                     data-testid="wholesale-arv-confidence" className="inp" />
            </F>
          </div>
          <F label="Repair cost">
            <input type="number" min="0" step="500" value={inputs.repairCost}
                   onChange={setInput("repairCost")}
                   data-testid="wholesale-input-repairs" className="inp" />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Buyer rule %" hint="0.70 = 70%">
              <input type="number" min="0" max="1" step="0.01" value={inputs.buyerRulePercent}
                     onChange={setInput("buyerRulePercent")}
                     data-testid="wholesale-buyer-rule" className="inp" />
            </F>
            <F label="Target fee">
              <input type="number" min="0" step="500" value={inputs.targetAssignmentFee}
                     onChange={setInput("targetAssignmentFee")}
                     data-testid="wholesale-target-fee" className="inp" />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <F label="Buyer closing costs">
              <input type="number" min="0" step="500" value={inputs.buyerClosingCosts}
                     onChange={setInput("buyerClosingCosts")}
                     data-testid="wholesale-bcc" className="inp" />
            </F>
            <F label="Offer cushion">
              <input type="number" min="0" step="500" value={inputs.offerCushion}
                     onChange={setInput("offerCushion")}
                     data-testid="wholesale-cushion" className="inp" />
            </F>
          </div>
          <F label="Seller asking (optional)">
            <input type="number" min="0" step="1000" value={inputs.askingPrice}
                   onChange={setInput("askingPrice")}
                   data-testid="wholesale-asking" className="inp" />
          </F>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={() => submit(false)} disabled={calculating}
                  data-testid="wholesale-calculate"
                  className="flex-1 rounded-md bg-amber-500 text-black text-sm font-semibold py-2 hover:bg-amber-400 disabled:opacity-50">
            {calculating ? <span className="inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin"/>Calculating…</span> : "Calculate"}
          </button>
          <button onClick={() => submit(true)} disabled={saving || !result}
                  data-testid="wholesale-save"
                  className="rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-900 px-3 py-2 hover:border-slate-400 disabled:opacity-40">
            <Save size={13} className="inline mr-1"/>Save
          </button>
        </div>
      </div>

      {/* Right column — results */}
      <div className="space-y-4">
        {!outputs ? (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-600">
            Enter your assumptions and click <b className="text-slate-900">Calculate</b> to see results.
          </div>
        ) : (
          <>
            <div className={`rounded-xl border p-5 ${CLASS_COLORS[classification] || CLASS_COLORS.review}`}
                 data-testid="wholesale-result-banner">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">Recommended Offer</div>
                  <div className="text-3xl font-bold tabular-nums" data-testid="wholesale-recommended-offer">
                    ${fmt(outputs.recommendedOffer)}
                  </div>
                </div>
                <div className="text-right"
                     data-testid="wholesale-deal-score-block"
                     data-classification={classification}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">Deal Score</div>
                  <div className="flex items-center gap-2 justify-end mt-0.5">
                    <span className={`inline-block w-3 h-3 rounded-full ${(CLASS_MAP[classification] || CLASS_MAP.review).dot}`}
                          aria-hidden="true"/>
                    <div className="text-3xl font-bold tabular-nums leading-none" data-testid="wholesale-deal-score">
                      {outputs.dealScore}
                    </div>
                  </div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider mt-1.5"
                       data-testid="wholesale-classification-label">
                    {(CLASS_MAP[classification] || CLASS_MAP.review).label}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mt-1.5"
                       data-testid="wholesale-confidence-line">
                    Confidence {Math.round((parseFloat(inputs.arvConfidence) || 0) * 100)}%
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <KVCard label="End Buyer MAO" value={`$${fmt(outputs.buyerMAO)}`} testid="wholesale-buyer-mao"/>
              <KVCard label="Seller MAO" value={`$${fmt(outputs.sellerMAO)}`} testid="wholesale-seller-mao"/>
              <KVCard label="Assignment Fee" value={`$${fmt(outputs.actualAssignmentFee)}`} testid="wholesale-assignment-fee"/>
              <KVCard label="Buyer All-In" value={`$${fmt(outputs.buyerAllInCost)}`}/>
              <KVCard label="Buyer Equity" value={`$${fmt(outputs.buyerEquity)}`}/>
              <KVCard label="Buyer ROI" value={outputs.buyerROI ? `${(parseFloat(outputs.buyerROI)*100).toFixed(1)}%` : "N/A"}/>
              <KVCard label="Seller Net Proceeds" value={`$${fmt(outputs.sellerNetProceeds)}`}/>
              <KVCard label="Repair-to-ARV" value={outputs.repairToARV ? `${(parseFloat(outputs.repairToARV)*100).toFixed(1)}%` : "—"}/>
            </div>

            {result?.warnings?.length > 0 && (
              <div data-testid="wholesale-warnings" className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-1">
                <div className="text-[11px] uppercase tracking-wider text-amber-700">Warnings</div>
                {result.warnings.map((w, i) => (
                  <div key={i} className="text-xs text-slate-700">
                    <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 mr-2">
                      {(w.severity || "info").toUpperCase()}
                    </span>
                    {w.message}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button onClick={generateAi} disabled={aiLoading || !scenario}
                      data-testid="wholesale-generate-ai"
                      className="rounded-md border border-purple-300 bg-purple-50 text-sm text-purple-800 px-3 py-2 hover:border-purple-500 disabled:opacity-40">
                {aiLoading ? <span className="inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin"/>Analyzing…</span> :
                  <span className="inline-flex items-center gap-2"><Sparkles size={13}/>Generate AI Analysis</span>}
              </button>
              <button onClick={exportPdf} disabled={!scenario}
                      data-testid="wholesale-export-pdf"
                      className="rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-900 px-3 py-2 hover:border-slate-400 disabled:opacity-40">
                <Download size={13} className="inline mr-1"/>Export Internal PDF
              </button>
              {scenario && (
                <span className="text-[11px] text-slate-500 self-center ml-2">
                  Saved · scenario <code className="text-slate-700">{scenario.id.slice(0,8)}</code>
                </span>
              )}
            </div>

            {scenario?.ai_recommendation && (
              <div className="rounded-xl border border-purple-300 bg-purple-50 p-4 space-y-2"
                   data-testid="wholesale-ai-block">
                <div className="text-[11px] uppercase tracking-wider text-purple-700">AI Recommendation</div>
                <div className="text-sm text-slate-900">{scenario.ai_recommendation.summary}</div>
                {scenario.ai_recommendation.strengths?.length > 0 && (
                  <BulletBlock title="Strengths" items={scenario.ai_recommendation.strengths}/>
                )}
                {scenario.ai_recommendation.risks?.length > 0 && (
                  <BulletBlock title="Risks" items={scenario.ai_recommendation.risks}/>
                )}
                <div className="text-[10px] text-slate-500 italic">{scenario.ai_recommendation.disclaimer}</div>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .inp { width:100%; background:#ffffff; border:1px solid #e2e8f0;
               border-radius:6px; padding:6px 10px; font-size:13px; color:#0f172a;
               outline:none; transition:border-color .15s ease, box-shadow .15s ease; }
        .inp:focus { border-color:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,0.15); }
      `}</style>
    </div>
  );
}

function F({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        {hint && <span className="text-[9px] text-slate-600">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function KVCard({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function BulletBlock({ title, items }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-700 mt-1">{title}</div>
      <ul className="text-xs text-slate-600 mt-0.5 space-y-0.5 list-disc list-inside">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}

function fmt(v) {
  if (v === null || v === undefined || v === "") return "0";
  const n = parseFloat(String(v));
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
